<template>
  <div>
    <head>
      <meta charset="utf-8"/>
      <meta content="width=device-width, initial-scale=1.0" name="viewport">
      <meta content="Invoice Approvals System" name="description"/>
      <link href="assets/images/favicon.png" rel="shortcut icon">
      <link href="assets/libs/datatables.net-bs4/css/dataTables.bootstrap4.min.css" rel="stylesheet" type="text/css"/>
      <link href="assets/libs/datatables.net-buttons-bs4/css/buttons.bootstrap4.min.css" rel="stylesheet" type="text/css"/>
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css"/>
      <link href="https://cdn.jsdelivr.net/npm/bootstrap-select@1.13.14/dist/css/bootstrap-select.min.css" rel="stylesheet">
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css"/>
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css"/>
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css"/>
    </head>

    <body data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar/>

      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">

            <!-- Page Header -->
            <div class="row mb-3">
              <div class="col-12">
                <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
                  <div>
                    <h4 class="mb-1 fw-semibold">Invoice Approvals</h4>
                    <nav aria-label="breadcrumb">
                      <ol class="breadcrumb mb-0">
                        <li class="breadcrumb-item">
                          <a @click="gotTo('dashboard')" href="javascript:void(0)" class="text-primary">
                            <i class="bx bx-home-alt me-1"></i>Dashboard
                          </a>
                        </li>
                        <li class="breadcrumb-item active">Invoice Approvals</li>
                      </ol>
                    </nav>
                  </div>

                  <div class="d-flex gap-2">
                    <button v-if="activeTab === 'approved' && getRight('DOWNLOAD APPROVED INVOICES')"
                            @click="downloadApprovedBills()"
                            class="btn btn-outline-success d-flex align-items-center"
                            :disabled="approvedBills.length === 0">
                      <i class="bx bx-download me-1"></i>
                      <span class="d-none d-sm-inline">Export</span>
                    </button>
                    <button @click="refreshData()"
                            class="btn btn-primary d-flex align-items-center"
                            :disabled="loading">
                      <i class="bx bx-refresh me-1" :class="{ 'bx-spin': loading }"></i>
                      <span class="d-none d-sm-inline">Refresh</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- tool bar -->
            <ToolBarPlot/>
            <!-- end of toolbar -->

            <!-- Summary Stats Cards -->
            <div class="row mb-3">
              <!-- Pending Card - only visible if user has PENDING INVOICE APPROVALS TAB right -->
              <div class="col-xl-3 col-md-6" v-if="getRight('PENDING INVOICE APPROVALS TAB')">
                <div class="card stats-card border-0 shadow-sm cursor-pointer"
                     :class="{ 'ring-warning': activeTab === 'pending' }"
                     @click="switchTab('pending')">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-warning-subtle text-warning rounded-circle fs-3">
                            <i class="bx bx-time-five"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1 text-uppercase" style="font-size: 11px; letter-spacing: 0.5px;">Pending Approval</p>
                        <h4 class="mb-0 fw-bold text-warning">{{ numberFormat(pendingSummary.count) }}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Approved Card - only visible if user has APPROVED INVOICES TAB right -->
              <div class="col-xl-3 col-md-6" v-if="getRight('APPROVED INVOICES TAB')">
                <div class="card stats-card border-0 shadow-sm cursor-pointer"
                     :class="{ 'ring-success': activeTab === 'approved' }"
                     @click="switchTab('approved')">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-success-subtle text-success rounded-circle fs-3">
                            <i class="bx bx-check-circle"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1 text-uppercase" style="font-size: 11px; letter-spacing: 0.5px;">Approved</p>
                        <h4 class="mb-0 fw-bold text-success">{{ numberFormat(approvedSummary.count) }}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Value Card - visible to anyone who has at least one tab right -->
              <div class="col-xl-3 col-md-6" v-if="getRight('PENDING INVOICE APPROVALS TAB') || getRight('APPROVED INVOICES TAB')">
                <div class="card stats-card border-0 shadow-sm">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-primary-subtle text-primary rounded-circle fs-3">
                            <i class="bx bx-money"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1 text-uppercase" style="font-size: 11px; letter-spacing: 0.5px;">
                          {{ activeTab === 'pending' ? 'Pending Value' : 'Approved Value' }}
                        </p>
                        <h4 class="mb-0 fw-bold">{{ formatPrice(activeTab === 'pending' ? pendingSummary.total : approvedSummary.total) }}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card stats-card border-0 shadow-sm">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-info-subtle text-info rounded-circle fs-3">
                            <i class="bx bx-calendar"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1 text-uppercase" style="font-size: 11px; letter-spacing: 0.5px;">Period</p>
                        <h6 class="mb-0 fw-semibold">{{ formatDateShort(dateFrom) }} - {{ formatDateShort(dateTo) }}</h6>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Main Card with Tabs -->
            <div class="row">
              <div class="col-12">
                <div class="card border-0 shadow-sm">

                  <!-- Tab Navigation -->
                  <div class="card-header bg-white border-bottom p-0">
                    <ul class="nav nav-tabs nav-tabs-custom" role="tablist">
                      <li class="nav-item" v-if="getRight('PENDING INVOICE APPROVALS TAB')">
                        <a class="nav-link py-3 px-4"
                           :class="{ 'active': activeTab === 'pending' }"
                           @click="switchTab('pending')"
                           href="javascript:void(0)">
                          <i class="bx bx-time-five me-2"></i>
                          <span>Pending Approval</span>
                          <span v-if="pendingSummary.count > 0" class="badge bg-warning text-dark ms-2">
                            {{ pendingSummary.count }}
                          </span>
                        </a>
                      </li>
                      <li class="nav-item" v-if="getRight('APPROVED INVOICES TAB')">
                        <a class="nav-link py-3 px-4"
                           :class="{ 'active': activeTab === 'approved' }"
                           @click="switchTab('approved')"
                           href="javascript:void(0)">
                          <i class="bx bx-check-circle me-2"></i>
                          <span>Approved</span>
                          <span v-if="approvedSummary.count > 0" class="badge bg-success ms-2">
                            {{ approvedSummary.count }}
                          </span>
                        </a>
                      </li>
                    </ul>
                  </div>

                  <!-- Collapsible Filters -->
                  <div class="card-header bg-white border-bottom">
                    <div class="d-flex align-items-center justify-content-between">
                      <button class="btn btn-link text-dark text-decoration-none p-0 d-flex align-items-center"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target="#filterCollapse"
                              aria-expanded="true">
                        <i class="bx bx-filter-alt me-2 fs-5"></i>
                        <span class="fw-semibold">Filters</span>
                        <span v-if="hasActiveFilters" class="badge bg-primary ms-2">{{ activeFilterCount }}</span>
                        <i class="bx bx-chevron-down ms-2 filter-chevron"></i>
                      </button>
                      <button v-if="hasActiveFilters"
                              @click="clearFilters"
                              class="btn btn-sm btn-outline-secondary">
                        <i class="bx bx-x me-1"></i>Clear All
                      </button>
                    </div>
                  </div>

                  <div class="collapse show" id="filterCollapse">
                    <div class="card-body border-bottom bg-light py-3">
                      <div class="row g-3">
                        <!-- Search -->
                        <div class="col-lg-4 col-md-6">
                          <label class="form-label small text-muted mb-1">Search</label>
                          <div class="input-group">
                            <span class="input-group-text bg-white border-end-0">
                              <i class="bx bx-search text-muted"></i>
                            </span>
                            <input v-model="search"
                                   @input="debounceSearch"
                                   class="form-control border-start-0 ps-0"
                                   placeholder="Bill no, customer name..."
                                   type="text">
                            <button v-if="search"
                                    @click="search = ''; refreshData()"
                                    class="btn btn-outline-secondary border-start-0"
                                    type="button">
                              <i class="bx bx-x"></i>
                            </button>
                          </div>
                        </div>

                        <!-- Date Range -->
                        <div class="col-lg-4 col-md-6">
                          <label class="form-label small text-muted mb-1">Date Range</label>
                          <div class="input-group">
                            <input v-model="dateFrom" class="form-control" type="date">
                            <span class="input-group-text bg-white">to</span>
                            <input v-model="dateTo" class="form-control" type="date">
                          </div>
                        </div>

                        <!-- Quick Date Filters -->
                        <div class="col-lg-4 col-md-12">
                          <label class="form-label small text-muted mb-1">Quick Select</label>
                          <div class="btn-group w-100" role="group">
                            <button @click="setDateRange('today')"
                                    type="button"
                                    class="btn btn-outline-secondary btn-sm"
                                    :class="{ 'active': quickDateRange === 'today' }">Today</button>
                            <button @click="setDateRange('week')"
                                    type="button"
                                    class="btn btn-outline-secondary btn-sm"
                                    :class="{ 'active': quickDateRange === 'week' }">Week</button>
                            <button @click="setDateRange('month')"
                                    type="button"
                                    class="btn btn-outline-secondary btn-sm"
                                    :class="{ 'active': quickDateRange === 'month' }">Month</button>
                            <button @click="setDateRange('quarter')"
                                    type="button"
                                    class="btn btn-outline-secondary btn-sm"
                                    :class="{ 'active': quickDateRange === 'quarter' }">Quarter</button>
                          </div>
                        </div>

                        <!-- Location Filters -->
                        <div class="col-lg-3 col-md-6">
                          <label class="form-label small text-muted mb-1">Sub County</label>
                          <select class="form-select" v-model="subCounty">
                            <option selected="" value="">All Sub Counties</option>
                            <option :key="index" :value="item" v-for="(item, index) in subCounties">
                              {{ item.subCountyName }}
                            </option>
                          </select>
                        </div>

                        <div class="col-lg-3 col-md-6">
                          <label class="form-label small text-muted mb-1">Ward</label>
                          <select class="form-select" v-model="ward" :disabled="!subCounty.subCountyID">
                            <option selected="" value="">All Wards</option>
                            <option :key="index" :value="item" v-for="(item, index) in wards">
                              {{ item.wardName }}
                            </option>
                          </select>
                        </div>

                        <!-- Approver Filter (only for Approved tab) -->
                        <div class="col-lg-3 col-md-6" v-if="activeTab === 'approved'">
                          <label class="form-label small text-muted mb-1">Approved By</label>
                          <select class="form-select" v-model="approver">
                            <option value="">All Approvers</option>
                            <option v-for="(item, index) in approvers" :key="`approver-${index}`" :value="item.approvedBy">
                              {{ item.approvedBy }} ({{ item.count }})
                            </option>
                          </select>
                        </div>

                        <div class="col-lg-3 col-md-6 d-flex align-items-end">
                          <button @click="refreshData()" class="btn btn-primary w-100">
                            <i class="bx bx-filter me-1"></i> Apply Filters
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Tab Content -->
                  <div class="tab-content">

                    <!-- ===================== PENDING TAB ===================== -->
                    <div v-if="activeTab === 'pending' && getRight('PENDING INVOICE APPROVALS TAB')" class="tab-pane active">
                      <div class="card-body p-0">
                        <!-- Loading State -->
                        <div v-if="loading" class="text-center py-5">
                          <div class="spinner-border text-warning mb-3" role="status" style="width: 3rem; height: 3rem;">
                            <span class="visually-hidden">Loading...</span>
                          </div>
                          <h6 class="text-muted">Loading pending invoices...</h6>
                        </div>

                        <!-- Empty State -->
                        <div v-else-if="pendingBills.length === 0" class="text-center py-5">
                          <div class="mb-3">
                            <i class="bx bx-check-shield text-success" style="font-size: 4rem;"></i>
                          </div>
                          <h5 class="text-success">All Caught Up!</h5>
                          <p class="text-muted mb-3">No invoices pending approval</p>
                          <button @click="clearFilters" class="btn btn-outline-primary btn-sm">
                            <i class="bx bx-reset me-1"></i> Reset Filters
                          </button>
                        </div>

                        <!-- Pending Table -->
                        <div v-else class="table-responsive">
                          <table class="table table-hover align-middle mb-0">
                            <thead class="table-light">
                            <tr>
                              <th class="text-muted fw-semibold" style="width: 50px;">#</th>
                              <th class="text-muted fw-semibold">Invoice Details</th>
                              <th class="text-muted fw-semibold d-none d-lg-table-cell">Location</th>
                              <th class="text-muted fw-semibold text-end">Amount</th>
                              <th class="text-muted fw-semibold d-none d-md-table-cell">Created</th>
                              <th class="text-muted fw-semibold text-center" style="width: 140px;">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(item, index) in pendingBills" :key="'pending-' + index" class="bill-row-pending">
                              <td class="text-muted">{{ ((pendingCurrentPage - 1) * pageSize) + index + 1 }}</td>
                              <td>
                                <div class="d-flex align-items-start">
                                  <div class="flex-shrink-0 me-3">
                                    <div class="avatar-sm">
                                        <span class="avatar-title bg-warning-subtle text-warning rounded-circle">
                                          <i class="bx bx-time"></i>
                                        </span>
                                    </div>
                                  </div>
                                  <div class="flex-grow-1 min-width-0">
                                    <h6 class="mb-1 text-truncate">
                                      <span class="text-primary fw-semibold">{{ item.billNo }}</span>
                                    </h6>
                                    <p class="text-muted mb-1 small text-truncate">
                                      {{ item.customerName || item.description }}
                                    </p>
                                    <span class="badge bg-light text-dark small">
                                        {{ item.incomeTypeDescription }}
                                      </span>
                                  </div>
                                </div>
                              </td>
                              <td class="d-none d-lg-table-cell">
                                <div class="small">
                                  <div class="fw-medium">{{ item.subCountyName }}</div>
                                  <div class="text-muted">{{ item.wardName }}</div>
                                </div>
                              </td>
                              <td class="text-end">
                                <span class="fw-bold text-primary">{{ formatPrice(item.detailAmount) }}</span>
                              </td>
                              <td class="d-none d-md-table-cell">
                                <div class="small">
                                  <div class="fw-medium">{{ item.names }}</div>
                                  <div class="text-muted">{{ formatDateShort(item.dateCreated) }}</div>
                                </div>
                              </td>
                              <td class="text-center">
                                <div class="btn-group" role="group">
                                  <button @click="viewBillDetails(item, 'pending')"
                                          class="btn btn-sm btn-soft-info"
                                          data-bs-toggle="modal"
                                          data-bs-target="#billDetailModal"
                                          title="View Details">
                                    <i class="bx bx-show"></i>
                                  </button>
                                  <button v-if="getRight('APPROVE INVOICE')"
                                          @click="approveBill(item)"
                                          class="btn btn-sm btn-soft-success"
                                          title="Approve Invoice">
                                    <i class="bx bx-check"></i>
                                  </button>
                                  <button v-if="getRight('REJECT INVOICE')"
                                          @click="rejectBill(item)"
                                          class="btn btn-sm btn-soft-danger"
                                          title="Reject Invoice">
                                    <i class="bx bx-x"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <!-- Pending Pagination -->
                      <div v-if="pendingBills.length > 0" class="card-footer bg-white border-top">
                        <div class="d-flex flex-wrap align-items-center justify-content-between gap-3">
                          <div class="d-flex align-items-center gap-2">
                            <span class="text-muted small">Show</span>
                            <select class="form-select form-select-sm" v-model="pageSize" style="width: auto;">
                              <option value="10">10</option>
                              <option value="25">25</option>
                              <option value="50">50</option>
                              <option value="100">100</option>
                            </select>
                            <span class="text-muted small">of {{ numberFormat(pendingSummary.count) }} entries</span>
                          </div>
                          <nav aria-label="Page navigation">
                            <ul class="pagination pagination-sm mb-0">
                              <li class="page-item" :class="{ disabled: pendingCurrentPage === 1 }">
                                <a class="page-link" href="javascript:void(0)" @click="pendingPrevPage">
                                  <i class="bx bx-chevron-left"></i>
                                </a>
                              </li>
                              <li v-for="page in pagination(pendingCurrentPage, pendingTotalPages, 3)"
                                  :key="'pending-page-' + page"
                                  class="page-item"
                                  :class="{ active: pendingCurrentPage === page, disabled: page === '...' }">
                                <span v-if="page === '...'" class="page-link">{{ page }}</span>
                                <a v-else class="page-link" href="javascript:void(0)" @click="pendingGoToPage(page)">{{ page }}</a>
                              </li>
                              <li class="page-item" :class="{ disabled: pendingCurrentPage === pendingTotalPages || pendingTotalPages === 0 }">
                                <a class="page-link" href="javascript:void(0)" @click="pendingNextPage">
                                  <i class="bx bx-chevron-right"></i>
                                </a>
                              </li>
                            </ul>
                          </nav>
                        </div>
                      </div>
                    </div>

                    <!-- ===================== APPROVED TAB ===================== -->
                    <div v-if="activeTab === 'approved' && getRight('APPROVED INVOICES TAB')" class="tab-pane active">
                      <div class="card-body p-0">
                        <!-- Loading State -->
                        <div v-if="loading" class="text-center py-5">
                          <div class="spinner-border text-success mb-3" role="status" style="width: 3rem; height: 3rem;">
                            <span class="visually-hidden">Loading...</span>
                          </div>
                          <h6 class="text-muted">Loading approved invoices...</h6>
                        </div>

                        <!-- Empty State -->
                        <div v-else-if="approvedBills.length === 0" class="text-center py-5">
                          <div class="mb-3">
                            <i class="bx bx-file-blank text-muted" style="font-size: 4rem;"></i>
                          </div>
                          <h5 class="text-muted">No Approved Invoices Found</h5>
                          <p class="text-muted mb-3">Try adjusting your filters or date range</p>
                          <button @click="clearFilters" class="btn btn-outline-primary btn-sm">
                            <i class="bx bx-reset me-1"></i> Reset Filters
                          </button>
                        </div>

                        <!-- Approved Table -->
                        <div v-else class="table-responsive">
                          <table class="table table-hover align-middle mb-0">
                            <thead class="table-light">
                            <tr>
                              <th class="text-muted fw-semibold" style="width: 50px;">#</th>
                              <th class="text-muted fw-semibold">Invoice Details</th>
                              <th class="text-muted fw-semibold d-none d-lg-table-cell">Location</th>
                              <th class="text-muted fw-semibold text-end">Amount</th>
                              <th class="text-muted fw-semibold d-none d-md-table-cell">Approved</th>
                              <th class="text-muted fw-semibold text-center" style="width: 100px;">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(item, index) in approvedBills" :key="'approved-' + index" class="bill-row-approved">
                              <td class="text-muted">{{ ((approvedCurrentPage - 1) * pageSize) + index + 1 }}</td>
                              <td>
                                <div class="d-flex align-items-start">
                                  <div class="flex-shrink-0 me-3">
                                    <div class="avatar-sm">
                                        <span class="avatar-title bg-success-subtle text-success rounded-circle">
                                          <i class="bx bx-check"></i>
                                        </span>
                                    </div>
                                  </div>
                                  <div class="flex-grow-1 min-width-0">
                                    <h6 class="mb-1 text-truncate">
                                      <span class="text-success fw-semibold">{{ item.billNo }}</span>
                                    </h6>
                                    <p class="text-muted mb-1 small text-truncate">
                                      {{ item.customerName || item.description }}
                                    </p>
                                    <span class="badge bg-light text-dark small">
                                        {{ item.incomeTypeDescription }}
                                      </span>
                                  </div>
                                </div>
                              </td>
                              <td class="d-none d-lg-table-cell">
                                <div class="small">
                                  <div class="fw-medium">{{ item.subCountyName }}</div>
                                  <div class="text-muted">{{ item.wardName }}</div>
                                </div>
                              </td>
                              <td class="text-end">
                                <span class="fw-bold text-success">{{ formatPrice(item.detailAmount) }}</span>
                              </td>
                              <td class="d-none d-md-table-cell">
                                <div class="small">
                                  <div class="fw-medium text-success">{{ item.approvedBy }}</div>
                                  <div class="text-muted">{{ formatDateShort(item.dateApproved) }}</div>
                                </div>
                              </td>
                              <td class="text-center">
                                <div class="btn-group" role="group">
                                  <button @click="viewBillDetails(item, 'approved')"
                                          class="btn btn-sm btn-soft-info"
                                          data-bs-toggle="modal"
                                          data-bs-target="#billDetailModal"
                                          title="View Details">
                                    <i class="bx bx-show"></i>
                                  </button>
                                  <button @click="printBill(item.billNo)"
                                          class="btn btn-sm btn-soft-primary"
                                          title="Print Invoice">
                                    <i class="bx bx-printer"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <!-- Approved Pagination -->
                      <div v-if="approvedBills.length > 0" class="card-footer bg-white border-top">
                        <div class="d-flex flex-wrap align-items-center justify-content-between gap-3">
                          <div class="d-flex align-items-center gap-2">
                            <span class="text-muted small">Show</span>
                            <select class="form-select form-select-sm" v-model="pageSize" style="width: auto;">
                              <option value="10">10</option>
                              <option value="25">25</option>
                              <option value="50">50</option>
                              <option value="100">100</option>
                            </select>
                            <span class="text-muted small">of {{ numberFormat(approvedSummary.count) }} entries</span>
                          </div>
                          <nav aria-label="Page navigation">
                            <ul class="pagination pagination-sm mb-0">
                              <li class="page-item" :class="{ disabled: approvedCurrentPage === 1 }">
                                <a class="page-link" href="javascript:void(0)" @click="approvedPrevPage">
                                  <i class="bx bx-chevron-left"></i>
                                </a>
                              </li>
                              <li v-for="page in pagination(approvedCurrentPage, approvedTotalPages, 3)"
                                  :key="'approved-page-' + page"
                                  class="page-item"
                                  :class="{ active: approvedCurrentPage === page, disabled: page === '...' }">
                                <span v-if="page === '...'" class="page-link">{{ page }}</span>
                                <a v-else class="page-link" href="javascript:void(0)" @click="approvedGoToPage(page)">{{ page }}</a>
                              </li>
                              <li class="page-item" :class="{ disabled: approvedCurrentPage === approvedTotalPages || approvedTotalPages === 0 }">
                                <a class="page-link" href="javascript:void(0)" @click="approvedNextPage">
                                  <i class="bx bx-chevron-right"></i>
                                </a>
                              </li>
                            </ul>
                          </nav>
                        </div>
                      </div>
                    </div>

                    <!-- ===================== NO ACCESS STATE ===================== -->
                    <div v-if="(activeTab === 'pending' && !getRight('PENDING INVOICE APPROVALS TAB')) || (activeTab === 'approved' && !getRight('APPROVED INVOICES TAB'))" class="tab-pane active">
                      <div class="card-body p-0">
                        <div class="text-center py-5">
                          <div class="mb-3">
                            <i class="bx bx-lock-alt text-danger" style="font-size: 4rem;"></i>
                          </div>
                          <h5 class="text-danger">Access Denied</h5>
                          <p class="text-muted mb-0">You do not have permission to view this section.</p>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ===================== BILL DETAIL MODAL ===================== -->
        <div class="modal fade" id="billDetailModal" tabindex="-1" aria-labelledby="billDetailModalLabel" aria-hidden="true">
          <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content border-0 shadow">
              <div class="modal-header border-0 pb-0">
                <div>
                  <span class="badge mb-2"
                        :class="selectedBillType === 'pending' ? 'bg-warning-subtle text-warning' : 'bg-success-subtle text-success'">
                    <i class="bx me-1" :class="selectedBillType === 'pending' ? 'bx-time' : 'bx-check-circle'"></i>
                    {{ selectedBillType === 'pending' ? 'Pending Approval' : 'Approved' }}
                  </span>
                  <h5 class="modal-title fw-bold" id="billDetailModalLabel">{{ selectedBill.billNo }}</h5>
                </div>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>

              <div class="modal-body pt-2">
                <!-- Amount Highlight -->
                <div class="rounded-3 p-4 text-center mb-4"
                     :class="selectedBillType === 'pending' ? 'bg-warning-subtle' : 'bg-success-subtle'">
                  <p class="text-muted mb-1 small text-uppercase">Invoice Amount</p>
                  <h2 class="fw-bold mb-0" :class="selectedBillType === 'pending' ? 'text-warning' : 'text-success'">
                    {{ formatPrice(selectedBill.detailAmount) }}
                  </h2>
                </div>

                <!-- Details Grid -->
                <div class="row g-4">
                  <!-- Customer Info -->
                  <div class="col-md-6">
                    <div class="border rounded-3 p-3 h-100">
                      <h6 class="text-muted text-uppercase small mb-3">
                        <i class="bx bx-user me-2"></i>Customer
                      </h6>
                      <p class="fw-semibold mb-1">{{ selectedBill.customerName || selectedBill.description }}</p>
                      <p class="text-muted mb-0 small">{{ maskPhoneNumber(selectedBill.customerPhoneNumber) }}</p>
                    </div>
                  </div>

                  <!-- Creator/Approver Info -->
                  <div class="col-md-6">
                    <div class="border rounded-3 p-3 h-100">
                      <h6 class="text-muted text-uppercase small mb-3">
                        <i class="bx me-2" :class="selectedBillType === 'pending' ? 'bx-user-plus' : 'bx-check-shield'"></i>
                        {{ selectedBillType === 'pending' ? 'Created By' : 'Approval' }}
                      </h6>
                      <p class="fw-semibold mb-1" :class="selectedBillType === 'approved' ? 'text-success' : ''">
                        {{ selectedBillType === 'pending' ? selectedBill.names : selectedBill.approvedBy }}
                      </p>
                      <p class="text-muted mb-0 small">
                        {{ formatDateTime(selectedBillType === 'pending' ? selectedBill.dateCreated : selectedBill.dateApproved) }}
                      </p>
                    </div>
                  </div>

                  <!-- Location -->
                  <div class="col-md-6">
                    <div class="border rounded-3 p-3 h-100">
                      <h6 class="text-muted text-uppercase small mb-3">
                        <i class="bx bx-map me-2"></i>Location
                      </h6>
                      <p class="fw-semibold mb-1">{{ selectedBill.subCountyName }}</p>
                      <p class="text-muted mb-0 small">{{ selectedBill.wardName }} {{ selectedBill.zone ? '• ' + selectedBill.zone : '' }}</p>
                    </div>
                  </div>

                  <!-- Service -->
                  <div class="col-md-6">
                    <div class="border rounded-3 p-3 h-100">
                      <h6 class="text-muted text-uppercase small mb-3">
                        <i class="bx bx-receipt me-2"></i>Service
                      </h6>
                      <p class="fw-semibold mb-1">{{ selectedBill.incomeTypeDescription }}</p>
                      <p class="text-muted mb-0 small">{{ selectedBill.feeDescription }}</p>
                    </div>
                  </div>
                </div>

                <!-- Timeline (for approved bills) -->
                <div v-if="selectedBillType === 'approved'" class="mt-4 pt-3 border-top">
                  <h6 class="text-muted text-uppercase small mb-3">
                    <i class="bx bx-time me-2"></i>Timeline
                  </h6>
                  <div class="d-flex align-items-center gap-4 flex-wrap">
                    <div class="d-flex align-items-center">
                      <span class="badge bg-light text-dark rounded-pill me-2">1</span>
                      <div>
                        <p class="mb-0 small fw-medium">Created</p>
                        <p class="mb-0 text-muted small">{{ formatDateTime(selectedBill.dateCreated) }}</p>
                      </div>
                    </div>
                    <div class="text-muted">
                      <i class="bx bx-right-arrow-alt"></i>
                    </div>
                    <div class="d-flex align-items-center">
                      <span class="badge bg-success rounded-pill me-2">2</span>
                      <div>
                        <p class="mb-0 small fw-medium text-success">Approved</p>
                        <p class="mb-0 text-muted small">{{ formatDateTime(selectedBill.dateApproved) }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="modal-footer border-0 pt-0">
                <button class="btn btn-light" data-bs-dismiss="modal" type="button">
                  Close
                </button>
                <button v-if="selectedBillType === 'pending' && getRight('APPROVE INVOICE')"
                        @click="approveBill(selectedBill)"
                        class="btn btn-success"
                        data-bs-dismiss="modal"
                        type="button">
                  <i class="bx bx-check me-1"></i> Approve
                </button>
                <button v-if="selectedBillType === 'approved'"
                        @click="printBill(selectedBill.billNo)"
                        class="btn btn-primary"
                        type="button">
                  <i class="bx bx-printer me-1"></i> Print Invoice
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- ===================== REJECT MODAL ===================== -->
        <div class="modal fade" id="rejectModal" tabindex="-1" aria-labelledby="rejectModalLabel" aria-hidden="true">
          <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content border-0 shadow">
              <div class="modal-header border-0 pb-0">
                <div>
                  <span class="badge bg-danger-subtle text-danger mb-2">
                    <i class="bx bx-x-circle me-1"></i>Reject Invoice
                  </span>
                  <h5 class="modal-title fw-bold" id="rejectModalLabel">{{ billToReject.billNo }}</h5>
                </div>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div class="modal-body">
                <div class="alert alert-danger d-flex align-items-start mb-3">
                  <i class="bx bx-error-circle fs-4 me-2 mt-1"></i>
                  <div>
                    <strong>Are you sure?</strong>
                    <p class="mb-0 small">This will reject the invoice and notify the creator.</p>
                  </div>
                </div>
                <div class="mb-3">
                  <label class="form-label">Rejection Reason <span class="text-danger">*</span></label>
                  <textarea v-model="rejectReason" class="form-control" rows="3"
                            placeholder="Please provide a reason for rejection..."></textarea>
                </div>
              </div>
              <div class="modal-footer border-0 pt-0">
                <button class="btn btn-light" data-bs-dismiss="modal" type="button">Cancel</button>
                <button @click="confirmReject"
                        class="btn btn-danger"
                        :disabled="!rejectReason.trim()"
                        data-bs-dismiss="modal"
                        type="button">
                  <i class="bx bx-x me-1"></i> Reject Invoice
                </button>
              </div>
            </div>
          </div>
        </div>

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6"></div>
              <div class="col-sm-6">
                <div class="text-sm-end d-sm-block">
                  Developed by County
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import { biller, execute, getRight } from "@/api";
import NavigationBar from "@/components/Navigation";
import router from "@/router";
import moment from "moment";
import ToolBarPlot from "@/components/landrate/ToolBar.vue";

export default {
  name: "InvoiceApprovals",
  components: {ToolBarPlot, NavigationBar },
  data() {
    return {
      // Common
      loading: false,
      activeTab: 'pending',
      pageSize: 10,
      dateFrom: '',
      dateTo: '',
      search: '',
      approver: '',
      quickDateRange: 'month',
      message: '',
      searchTimeout: null,

      // Pending Tab
      pendingBills: [],
      pendingCurrentPage: 1,
      pendingTotalPages: 0,
      pendingSummary: { count: 0, total: 0 },

      // Approved Tab
      approvedBills: [],
      approvedCurrentPage: 1,
      approvedTotalPages: 0,
      approvedSummary: { count: 0, total: 0 },
      approvers: [],

      // Filters
      subCounties: [],
      wards: [],
      subCounty: { subCountyID: "", subCountyName: "" },
      ward: { wardID: "", wardName: "" },

      // Modal
      selectedBill: {},
      selectedBillType: 'pending',
      billToReject: {},
      rejectReason: ''
    }
  },

  watch: {
    pageSize() {
      this.refreshData();
    },
    subCounty() {
      this.getWards();
      this.refreshData();
    },
    ward() {
      this.refreshData();
    },
    approver() {
      if (this.activeTab === 'approved' && getRight('APPROVED INVOICES TAB')) {
        this.getApprovedBills();
      }
    },
    dateFrom() {
      if (this.dateFrom && this.dateTo) {
        this.quickDateRange = '';
        this.refreshData();
      }
    },
    dateTo() {
      if (this.dateFrom && this.dateTo) {
        this.quickDateRange = '';
        this.refreshData();
      }
    }
  },

  computed: {
    hasActiveFilters() {
      return this.search || this.subCounty.subCountyID || this.ward.wardID || this.approver;
    },
    activeFilterCount() {
      let count = 0;
      if (this.search) count++;
      if (this.subCounty.subCountyID) count++;
      if (this.ward.wardID) count++;
      if (this.approver) count++;
      return count;
    }
  },

  mounted() {
    this.setDateRange('month');
    this.getSubCounties();
    this.getApprovers();

    // Determine the default active tab based on user rights
    const hasPendingRight = getRight('PENDING INVOICE APPROVALS TAB');
    const hasApprovedRight = getRight('APPROVED INVOICES TAB');

    // Check for tab parameter in route first
    if (this.$route.query.tab === 'approved' && hasApprovedRight) {
      this.activeTab = 'approved';
    } else if (this.$route.query.tab === 'pending' && hasPendingRight) {
      this.activeTab = 'pending';
    } else if (hasPendingRight) {
      this.activeTab = 'pending';
    } else if (hasApprovedRight) {
      this.activeTab = 'approved';
    }

    // Only fetch data for tabs the user has rights to
    if (hasPendingRight) {
      this.getPendingBills();
    }
    if (hasApprovedRight) {
      this.getApprovedBills();
    }

    this.menuAccess();
  },

  methods: {
    getRight(type) {
      return getRight(type);
    },

    menuAccess() {
      const data = new FormData();
      data.append("function", "menuAccess");
      data.append("menu", "Invoice Approvals");
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute(data, biller).then(() => {}).catch(() => {});
    },

    switchTab(tab) {
      // Enforce permission check before switching tabs
      if (tab === 'pending' && !getRight('PENDING INVOICE APPROVALS TAB')) return;
      if (tab === 'approved' && !getRight('APPROVED INVOICES TAB')) return;

      this.activeTab = tab;
      // Update URL without reloading
      this.$router.replace({ query: { ...this.$route.query, tab: tab } }).catch(() => {});
    },

    refreshData() {
      if (this.activeTab === 'pending' && getRight('PENDING INVOICE APPROVALS TAB')) {
        this.getPendingBills();
      } else if (this.activeTab === 'approved' && getRight('APPROVED INVOICES TAB')) {
        this.getApprovedBills();
      }
      // Also refresh summaries for tabs user has access to
      this.getSummaries();
    },

    getSummaries() {
      // Only get pending summary if user has right
      if (getRight('PENDING INVOICE APPROVALS TAB')) {
        const pendingData = new FormData();
        pendingData.append("function", "getPendingBillsForApproval");
        pendingData.append("page", 1);
        pendingData.append("rows_per_page", 1);
        pendingData.append("dateFrom", this.dateFrom);
        pendingData.append("dateTo", this.dateTo);
        pendingData.append("subCountyID", this.subCounty.subCountyID || '');
        pendingData.append("wardID", this.ward.wardID || '');
        pendingData.append("search", this.search || '');

        execute(pendingData, biller).then((res) => {
          if (res.data.success) {
            this.pendingSummary = res.data.data.summary || { count: 0, total: 0 };
          }
        }).catch(() => {});
      }

      // Only get approved summary if user has right
      if (getRight('APPROVED INVOICES TAB')) {
        const approvedData = new FormData();
        approvedData.append("function", "getApprovedBills");
        approvedData.append("page", 1);
        approvedData.append("rows_per_page", 1);
        approvedData.append("dateFrom", this.dateFrom);
        approvedData.append("dateTo", this.dateTo);
        approvedData.append("subCountyID", this.subCounty.subCountyID || '');
        approvedData.append("wardID", this.ward.wardID || '');
        approvedData.append("approver", this.approver || '');
        approvedData.append("search", this.search || '');

        execute(approvedData, biller).then((res) => {
          if (res.data.success) {
            this.approvedSummary = res.data.data.summary || { count: 0, total: 0 };
          }
        }).catch(() => {});
      }
    },

    // ==================== PENDING BILLS ====================
    getPendingBills() {
      // Guard: only fetch if user has the right
      if (!getRight('PENDING INVOICE APPROVALS TAB')) return;

      this.pendingBills = [];
      this.loading = true;

      const data = new FormData();
      data.append("function", "getPendingBillsForApproval");
      data.append("page", this.pendingCurrentPage);
      data.append("rows_per_page", this.pageSize);
      data.append("dateFrom", this.dateFrom);
      data.append("dateTo", this.dateTo);
      data.append("subCountyID", this.subCounty.subCountyID || '');
      data.append("wardID", this.ward.wardID || '');
      data.append("search", this.search || '');

      execute(data, biller)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.pendingBills = res.data.data.billDetails || [];
              this.pendingSummary = res.data.data.summary || { count: 0, total: 0 };
              this.pendingTotalPages = Math.ceil(this.pendingSummary.count / this.pageSize);
            } else {
              this.message = res.data.message;
            }
          })
          .catch((e) => {
            this.loading = false;
            this.message = e.message;
          });
    },

    pendingPrevPage() {
      if (this.pendingCurrentPage > 1) {
        this.pendingCurrentPage--;
        this.getPendingBills();
      }
    },

    pendingNextPage() {
      if (this.pendingCurrentPage < this.pendingTotalPages) {
        this.pendingCurrentPage++;
        this.getPendingBills();
      }
    },

    pendingGoToPage(page) {
      if (page > 0 && page <= this.pendingTotalPages && page !== this.pendingCurrentPage) {
        this.pendingCurrentPage = page;
        this.getPendingBills();
      }
    },

    // ==================== APPROVED BILLS ====================
    getApprovedBills() {
      // Guard: only fetch if user has the right
      if (!getRight('APPROVED INVOICES TAB')) return;

      this.approvedBills = [];
      this.loading = true;

      const data = new FormData();
      data.append("function", "getApprovedBills");
      data.append("page", this.approvedCurrentPage);
      data.append("rows_per_page", this.pageSize);
      data.append("dateFrom", this.dateFrom);
      data.append("dateTo", this.dateTo);
      data.append("subCountyID", this.subCounty.subCountyID || '');
      data.append("wardID", this.ward.wardID || '');
      data.append("approver", this.approver || '');
      data.append("search", this.search || '');

      execute(data, biller)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.approvedBills = res.data.data.billDetails || [];
              this.approvedSummary = res.data.data.summary || { count: 0, total: 0 };
              this.approvedTotalPages = Math.ceil(this.approvedSummary.count / this.pageSize);
            } else {
              this.message = res.data.message;
            }
          })
          .catch((e) => {
            this.loading = false;
            this.message = e.message;
          });
    },

    approvedPrevPage() {
      if (this.approvedCurrentPage > 1) {
        this.approvedCurrentPage--;
        this.getApprovedBills();
      }
    },

    approvedNextPage() {
      if (this.approvedCurrentPage < this.approvedTotalPages) {
        this.approvedCurrentPage++;
        this.getApprovedBills();
      }
    },

    approvedGoToPage(page) {
      if (page > 0 && page <= this.approvedTotalPages && page !== this.approvedCurrentPage) {
        this.approvedCurrentPage = page;
        this.getApprovedBills();
      }
    },

    getApprovers() {
      const data = new FormData();
      data.append("function", "getApprovers");

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.approvers = res.data.data.approvers || [];
            }
          })
          .catch((e) => {
            console.error(e);
          });
    },

    // ==================== ACTIONS ====================
    viewBillDetails(bill, type) {
      this.selectedBill = bill;
      this.selectedBillType = type;
    },

    approveBill(bill) {
      if (!confirm(`Approve invoice ${bill.billNo}?\n\nAmount: ${this.formatPrice(bill.detailAmount)}\nCustomer: ${bill.customerName || bill.description}`)) {
        return;
      }

      this.loading = true;
      const data = new FormData();
      data.append("function", "approveBill");
      data.append("billNo", bill.billNo);
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

      execute(data, biller)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              alert(`Invoice ${bill.billNo} approved successfully!`);
              this.printBill(bill.billNo);
              this.getPendingBills();
              this.getApprovedBills();
            } else {
              alert(`Error: ${res.data.message}`);
            }
          })
          .catch((e) => {
            this.loading = false;
            alert(`Error: ${e.message}`);
          });
    },

    rejectBill(bill) {
      this.billToReject = bill;
      this.rejectReason = '';
      // Show reject modal
      const modal = new bootstrap.Modal(document.getElementById('rejectModal'));
      modal.show();
    },

    confirmReject() {
      if (!this.rejectReason.trim()) {
        alert('Please provide a rejection reason');
        return;
      }

      this.loading = true;
      const data = new FormData();
      data.append("function", "rejectBill");
      data.append("billNo", this.billToReject.billNo);
      data.append("reason", this.rejectReason);
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

      execute(data, biller)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              alert(`Invoice ${this.billToReject.billNo} has been rejected.`);
              this.getPendingBills();
            } else {
              alert(`Error: ${res.data.message}`);
            }
          })
          .catch((e) => {
            this.loading = false;
            alert(`Error: ${e.message}`);
          });
    },

    printBill(billNo) {
      localStorage['params'] = JSON.stringify({ billNo: billNo });
      const routeData = this.$router.resolve({ name: 'bill' });
      window.open(routeData.href, '_blank');
    },

    downloadApprovedBills() {
      if (!this.approvedBills || this.approvedBills.length === 0) {
        alert("No data to download");
        return;
      }

      let csv = 'Bill No,Customer,Income Type,Fee,Amount,Approved By,Approval Date,Sub County,Ward\n';

      this.approvedBills.forEach(bill => {
        csv += `"${bill.billNo}",`;
        csv += `"${bill.customerName || bill.description}",`;
        csv += `"${bill.incomeTypeDescription}",`;
        csv += `"${bill.feeDescription}",`;
        csv += `"${bill.detailAmount}",`;
        csv += `"${bill.approvedBy}",`;
        csv += `"${bill.dateApproved}",`;
        csv += `"${bill.subCountyName}",`;
        csv += `"${bill.wardName}"\n`;
      });

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `approved_invoices_${moment().format('YYYYMMDD')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    // ==================== FILTERS ====================
    setDateRange(range) {
      this.quickDateRange = range;
      switch (range) {
        case 'today':
          this.dateFrom = moment().format('YYYY-MM-DD');
          this.dateTo = moment().format('YYYY-MM-DD');
          break;
        case 'week':
          this.dateFrom = moment().startOf('week').format('YYYY-MM-DD');
          this.dateTo = moment().format('YYYY-MM-DD');
          break;
        case 'month':
          this.dateFrom = moment().startOf('month').format('YYYY-MM-DD');
          this.dateTo = moment().format('YYYY-MM-DD');
          break;
        case 'quarter':
          this.dateFrom = moment().startOf('quarter').format('YYYY-MM-DD');
          this.dateTo = moment().format('YYYY-MM-DD');
          break;
      }
    },

    clearFilters() {
      this.search = '';
      this.subCounty = { subCountyID: "", subCountyName: "" };
      this.ward = { wardID: "", wardName: "" };
      this.approver = '';
      this.setDateRange('month');
      this.pendingCurrentPage = 1;
      this.approvedCurrentPage = 1;
      if (getRight('PENDING INVOICE APPROVALS TAB')) this.getPendingBills();
      if (getRight('APPROVED INVOICES TAB')) this.getApprovedBills();
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.pendingCurrentPage = 1;
        this.approvedCurrentPage = 1;
        this.refreshData();
      }, 500);
    },

    getSubCounties() {
      const data = new FormData();
      data.append("function", "getSubCounty");
      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.subCounties = res.data.data.subCounties;
            }
          })
          .catch((e) => {
            this.message = e.message;
          });
    },

    getWards() {
      this.wards = [];
      this.ward = { wardID: '', wardName: '' };

      if (!this.subCounty.subCountyID) return;

      const data = new FormData();
      data.append("function", "getWards");
      data.append("subCountyID", this.subCounty.subCountyID);
      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.wards = res.data.data.wards;
            }
          })
          .catch((e) => {
            this.message = e.message;
          });
    },

    // ==================== UTILITIES ====================
    gotTo(route) {
      router.push(route);
    },

    maskPhoneNumber(input) {
      if (!input || typeof input !== 'string') return input;
      const digits = input.replace(/\D/g, '');
      if (digits.length > 4) {
        return input.slice(0, 2) + input.slice(2, -2).replace(/\d/g, '*') + input.slice(-2);
      }
      return input;
    },

    numberFormat(num) {
      return new Intl.NumberFormat("en-GB").format(num);
    },

    formatPrice(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(value);
    },

    formatDateTime(dateString) {
      if (!dateString) return '';
      return moment(dateString).format('DD MMM YYYY, hh:mm A');
    },

    formatDateShort(dateString) {
      if (!dateString) return '';
      return moment(dateString).format('DD MMM YYYY');
    },

    pagination(currentPage, totalPages, maxVisibleButtons) {
      let pages = [];
      let start = currentPage - Math.floor(maxVisibleButtons / 2);
      let end = currentPage + Math.floor(maxVisibleButtons / 2);

      if (start < 1) {
        start = 1;
        end = start + maxVisibleButtons - 1;
        if (end > totalPages) end = totalPages;
      }

      if (end > totalPages) {
        end = totalPages;
        start = end - maxVisibleButtons + 1;
        if (start < 1) start = 1;
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (start > 1) pages.unshift("...");
      if (end < totalPages) pages.push("...");

      return pages;
    }
  }
}
</script>

<style scoped>
/* Stats Cards */
.stats-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stats-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1) !important;
}

.cursor-pointer {
  cursor: pointer;
}

.ring-warning {
  box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.3) !important;
}

.ring-success {
  box-shadow: 0 0 0 3px rgba(25, 135, 84, 0.3) !important;
}

.avatar-sm {
  width: 2.5rem;
  height: 2.5rem;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 1.25rem;
}

/* Subtle background colors */
.bg-success-subtle { background-color: rgba(25, 135, 84, 0.1) !important; }
.bg-primary-subtle { background-color: rgba(13, 110, 253, 0.1) !important; }
.bg-info-subtle { background-color: rgba(13, 202, 240, 0.1) !important; }
.bg-warning-subtle { background-color: rgba(255, 193, 7, 0.15) !important; }
.bg-danger-subtle { background-color: rgba(220, 53, 69, 0.1) !important; }

/* Tab Navigation */
.nav-tabs-custom {
  border-bottom: none;
}

.nav-tabs-custom .nav-link {
  border: none;
  color: #6c757d;
  font-weight: 500;
  border-bottom: 3px solid transparent;
  transition: all 0.2s ease;
}

.nav-tabs-custom .nav-link:hover {
  color: #495057;
  background-color: rgba(0, 0, 0, 0.02);
}

.nav-tabs-custom .nav-link.active {
  color: #0d6efd;
  border-bottom-color: #0d6efd;
  background-color: transparent;
}

/* Filter chevron animation */
.filter-chevron {
  transition: transform 0.3s ease;
}

[aria-expanded="false"] .filter-chevron {
  transform: rotate(-90deg);
}

/* Table styles */
.bill-row-pending:hover {
  background-color: rgba(255, 193, 7, 0.05);
}

.bill-row-approved:hover {
  background-color: rgba(25, 135, 84, 0.05);
}

.min-width-0 {
  min-width: 0;
}

/* Soft buttons */
.btn-soft-info {
  background-color: rgba(13, 202, 240, 0.1);
  color: #0dcaf0;
  border: none;
}
.btn-soft-info:hover {
  background-color: rgba(13, 202, 240, 0.2);
  color: #0dcaf0;
}

.btn-soft-primary {
  background-color: rgba(13, 110, 253, 0.1);
  color: #0d6efd;
  border: none;
}
.btn-soft-primary:hover {
  background-color: rgba(13, 110, 253, 0.2);
  color: #0d6efd;
}

.btn-soft-success {
  background-color: rgba(25, 135, 84, 0.1);
  color: #198754;
  border: none;
}
.btn-soft-success:hover {
  background-color: rgba(25, 135, 84, 0.2);
  color: #198754;
}

.btn-soft-danger {
  background-color: rgba(220, 53, 69, 0.1);
  color: #dc3545;
  border: none;
}
.btn-soft-danger:hover {
  background-color: rgba(220, 53, 69, 0.2);
  color: #dc3545;
}

/* Card styles */
.card {
  border-radius: 0.75rem;
}

/* Form styles */
.form-select:focus,
.form-control:focus {
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
}

/* Pagination */
.pagination .page-link {
  border: none;
  border-radius: 0.375rem;
  margin: 0 2px;
  color: #6c757d;
}

.pagination .page-item.active .page-link {
  background-color: #0d6efd;
  color: white;
}

.pagination .page-link:hover {
  background-color: #e9ecef;
}

/* Modal */
.modal-content {
  border-radius: 1rem;
}

/* Responsive */
@media (max-width: 768px) {
  .stats-card .card-body {
    padding: 1rem;
  }

  .avatar-sm {
    width: 2rem;
    height: 2rem;
  }

  .avatar-title {
    font-size: 1rem;
  }

  .nav-tabs-custom .nav-link {
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
  }
}

/* Print styles */
@media print {
  .modal-header,
  .modal-footer,
  .card-header,
  .card-footer {
    display: none;
  }
}
</style>